import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Loader2, ShieldCheck, Activity, Search, CheckCircle2, Clock } from "lucide-react";
import { useGetGradeUpgradeDetailsQuery } from "../api/upgradeApi";

// Upgrade payment is confirmed asynchronously by VerifyUpgradePaymentJob, which
// flips the application pending_payment -> submitted. The queue worker can take
// up to ~1 minute (cron tick), so we poll patiently and never hard-fail: if it
// hasn't confirmed within the grace window we show a "still confirming" state
// rather than wrongly declaring failure.
const DONE_STATUSES = ["submitted", "under_review", "approved"];
const GRACE_SECONDS = 150;

export default function UpgradePaymentProcessingPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [waited, setWaited] = useState(0);

  const { data, refetch } = useGetGradeUpgradeDetailsQuery(id, {
    skip: !id,
    pollingInterval: 4000,
  });

  const status: string | undefined = data?.status;
  const confirmed = status ? DONE_STATUSES.includes(status) : false;
  const timedOut = waited >= GRACE_SECONDS;

  // Tick a counter so we know when the grace window has elapsed.
  useEffect(() => {
    if (confirmed) return;
    const t = setInterval(() => setWaited((w) => w + 1), 1000);
    return () => clearInterval(t);
  }, [confirmed]);

  // On confirmation, send them to the upgrade detail page after a short beat.
  useEffect(() => {
    if (!confirmed) return;
    const t = setTimeout(() => navigate(`/upgrade-applications/grade/${id}`, { replace: true }), 2500);
    return () => clearTimeout(t);
  }, [confirmed, id, navigate]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="max-w-md w-full relative">
        <div className="absolute -top-20 -left-20 w-64 h-64 bg-indigo-500/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-blue-500/5 rounded-full blur-3xl" />

        <div className="bg-white border border-gray-100 rounded-[3rem] shadow-2xl shadow-gray-200 overflow-hidden relative z-10">
          <div className="h-2 bg-gray-900 w-full relative overflow-hidden">
            <div className={`absolute inset-0 ${confirmed ? "bg-emerald-500" : "bg-indigo-500 animate-progress-slide"}`} />
          </div>

          <div className="p-10 md:p-14 text-center space-y-8">
            {/* Icon */}
            <div className="relative mx-auto w-24 h-24">
              {confirmed ? (
                <div className="relative z-10 w-full h-full rounded-[2.5rem] bg-emerald-50 border border-emerald-100 flex items-center justify-center">
                  <CheckCircle2 className="w-10 h-10 text-emerald-600" />
                </div>
              ) : (
                <>
                  <div className="absolute inset-0 rounded-[2.5rem] bg-indigo-50 animate-ping opacity-20" />
                  <div className="relative z-10 w-full h-full rounded-[2.5rem] bg-white border border-gray-100 shadow-inner flex items-center justify-center">
                    {timedOut ? <Clock className="w-10 h-10 text-amber-500" /> : <Activity className="w-10 h-10 text-indigo-600" />}
                  </div>
                </>
              )}
            </div>

            {/* Header */}
            <div className="space-y-3">
              <h2 className="text-2xl font-black uppercase tracking-tighter text-gray-900">
                {confirmed ? "Payment Confirmed" : timedOut ? "Still Confirming" : "Verifying Payment"}
              </h2>
              <div className="flex items-center justify-center gap-2 text-[9px] font-black text-gray-400 uppercase tracking-[0.3em]">
                <Search className="w-3 h-3" />
                {confirmed ? "Upgrade application submitted" : "Syncing with OnePay"}
              </div>
            </div>

            {/* Status block */}
            {confirmed ? (
              <p className="text-sm text-gray-600">
                Your upgrade fee has been received and your application is now submitted for review. Redirecting…
              </p>
            ) : timedOut ? (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Your payment is taking a little longer to confirm. This can happen briefly after payment — your
                  application will update automatically once confirmed. You can safely check back shortly.
                </p>
                <div className="flex items-center justify-center gap-2">
                  <button
                    onClick={() => refetch()}
                    className="px-4 py-2 rounded-lg border bg-white text-gray-700 text-sm font-medium hover:bg-gray-50"
                  >
                    Check again
                  </button>
                  <Link
                    to="/upgrade-applications"
                    className="px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800"
                  >
                    My applications
                  </Link>
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100 text-left space-y-3">
                <div className="flex justify-between items-center opacity-40">
                  <span className="text-[8px] font-black uppercase">Payment Received</span>
                  <span className="text-[8px] font-mono">OK</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[8px] font-black uppercase text-indigo-600">Awaiting Confirmation</span>
                  <Loader2 className="w-3 h-3 animate-spin text-indigo-600" />
                </div>
                <div className="pt-3 border-t border-gray-200/50 flex justify-between items-center italic">
                  <span className="text-[8px] font-black text-gray-400 uppercase">Application</span>
                  <span className="text-[9px] font-bold text-gray-900">#{id}</span>
                </div>
              </div>
            )}

            {!confirmed && !timedOut && (
              <p className="text-[10px] font-bold text-gray-400 leading-relaxed uppercase tracking-widest">
                Please do not refresh this page.<br />The transaction is being finalized.
              </p>
            )}
          </div>

          <div className="bg-gray-900 py-4 flex items-center justify-center gap-3">
            <ShieldCheck className="w-4 h-4 text-indigo-400" />
            <span className="text-[9px] font-black text-white uppercase tracking-[0.2em]">
              NIQS Encrypted Gateway
            </span>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes progress-slide { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }
        .animate-progress-slide { animation: progress-slide 2s infinite linear; width: 50%; }
      `}</style>
    </div>
  );
}
